import isCi from 'is-ci';
import { useCallback, useEffect, useRef } from 'react';
import { useInput as useInkInput, useStdin } from 'ink';
import useExit from './useExit';

type Handler = Parameters<typeof useInkInput>[0];
type Options = {
  isActive?: boolean;
};

type Extras = {
  onCtrlCPressed?: () => void;
};

type Subscription = {
  handler: Handler;
  isActive?: boolean;
};

const ctrlCSubscriptions = new Set<() => void>();
const exitHandlers = new Set<() => void>();

let activeStdin: NodeJS.ReadStream | null = null;
let stdinListenerCount = 0;

const onStdin = (data: Buffer) => {
  let input = String(data);
  let wasCtrlPressed = false;

  if (input <= '\u001A' && input !== '\r') {
    input = String.fromCharCode(input.charCodeAt(0) + 'a'.charCodeAt(0) - 1);
    wasCtrlPressed = true;
  }

  if (wasCtrlPressed && input === 'c') {
    ctrlCSubscriptions.forEach((subscription) => subscription());

    const { value: exit } = exitHandlers.values().next();
    if (exit) {
      setTimeout(exit, 10);
    }
  }
};

function normaliseOptions(options?: Options): Options {
  if (!options) {
    return {};
  }

  if ('isActive' in options) {
    return { isActive: options.isActive };
  }

  return {};
}

function normaliseExtras(extras?: Extras): Extras {
  if (!extras) {
    return {};
  }

  if ('onCtrlCPressed' in extras) {
    return { onCtrlCPressed: extras.onCtrlCPressed };
  }

  return {};
}

/*
  See https://github.com/vadimdemedes/pastel/issues/38
*/
export default function useInput(
  handler?: Handler,
  options: Options = {},
  extras: Extras = {},
) {
  const exit = useExit();
  const { stdin, isRawModeSupported } = useStdin();
  const subscriptionRef = useRef<Subscription[]>([]);

  const canUseRawInput = !isCi && Boolean(isRawModeSupported && stdin);

  const register = useCallback(
    (
      nextHandler: Handler,
      nextOptions: Options = {},
      nextExtras: Extras = {},
    ) => {
      if (!canUseRawInput) {
        return () => {};
      }

      const normalisedOptions = normaliseOptions(nextOptions);
      const normalisedExtras = normaliseExtras(nextExtras);

      const subscription: Subscription = {
        handler: nextHandler,
        isActive: normalisedOptions.isActive,
      };

      subscriptionRef.current = [...subscriptionRef.current, subscription];

      if (normalisedExtras.onCtrlCPressed) {
        ctrlCSubscriptions.add(normalisedExtras.onCtrlCPressed);
      }

      return () => {
        subscriptionRef.current = subscriptionRef.current.filter(
          (item) => item !== subscription,
        );

        if (normalisedExtras.onCtrlCPressed) {
          ctrlCSubscriptions.delete(normalisedExtras.onCtrlCPressed);
        }
      };
    },
    [canUseRawInput],
  );

  useEffect(() => {
    if (!canUseRawInput || !stdin) {
      return;
    }

    exitHandlers.add(exit);
    const onStdinData = (data: Buffer) => onStdin(data);

    if (stdinListenerCount === 0) {
      activeStdin = stdin;
      activeStdin.on('data', onStdinData);
    }

    stdinListenerCount += 1;

    return () => {
      exitHandlers.delete(exit);
      stdinListenerCount = Math.max(stdinListenerCount - 1, 0);

      if (stdinListenerCount === 0 && activeStdin) {
        activeStdin.off('data', onStdinData);
        activeStdin = null;
      }
    };
  }, [canUseRawInput, exit, stdin]);

  useInkInput(
    useCallback(
      (input, key) => {
        if (!canUseRawInput) {
          return;
        }

        for (const subscription of subscriptionRef.current) {
          if (subscription.isActive === false) {
            continue;
          }

          subscription.handler(input, key);
        }
      },
      [canUseRawInput],
    ),
    {
      isActive: canUseRawInput,
    },
  );

  useEffect(() => {
    if (!handler) {
      return;
    }

    const unsubscribe = register(handler, options, extras);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extras?.onCtrlCPressed, handler, options?.isActive, register]);

  if (handler) {
    return;
  }

  return register;
}
